import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import {
  ChevronLastIcon,
  ChevronsRight,
  ChevronsRightIcon,
  Edit,
  Eye,
  Trash,
} from "lucide-react";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import { Minus, Plus } from "lucide-react";
import AddProvinces from "./AddProvinces";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";

const Provinces = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [provinces, setProvinces] = useState([]);
  const [allProvinces, setAllProvinces] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);


  const fetchAllProvinces = async () => {
    try {
      const response = await fetchData(
        `provinces`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/provinces",
        dispatch
      );
      setAllProvinces(response?.data || []);
    } catch (error) {
      console.error("Error fetching all provinces:", error);
    }
  };


  const fetchProvinces = useCallback(async () => {
    try {
      setLoader(true);


      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };


      if (search && search.trim() !== "") {
        params.search = search.trim();
      }


      if (selectedProvince) {
        params.province_id = selectedProvince;
      }


      if (selectedStatus !== null && selectedStatus !== undefined) {
        params.status = selectedStatus;
      }

      const response = await fetchData(
        `provinces`,
        "GET",
        null,
        {},
        params,
        navigate,
        "/provinces",
        dispatch
      );

      setProvinces(response?.data || []);
      setPagination(prev => ({
        ...prev,
        totalItems: response?.pagination?.totalItems || 0,
        totalPages: response?.pagination?.totalPages || 0,
      }));

    } catch (error) {
      console.error("Error fetching provinces:", error);
      toast.error("Error fetching provinces");
    } finally {
      setLoader(false);
    }
  }, [pagination.currentPage, pagination.pageSize, search, selectedStatus, selectedProvince, navigate, dispatch]);


  useEffect(() => {
    const timeoutId = setTimeout(() => {

      if (pagination.currentPage !== 1) {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      } else {
        fetchProvinces();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search, selectedStatus, selectedProvince]);


  useEffect(() => {
    fetchProvinces();
  }, [pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    fetchAllProvinces();
    fetchProvinces();
  }, []);

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handleStatusChange = (selected) => {
    setSelectedStatus(selected);
  };

  const handleProvinceChange = (selected) => {
    setSelectedProvince(selected);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearch("");
    setSelectedStatus(null);
    setSelectedProvince(null);
  };

  const handleDelete = async () => {
    setModalVisible(false);

    setProvinces((prev) =>
      prev.filter((province) => province.province_id !== selectedUuid)
    );

    try {
      await fetchData(
        `provinces`,
        "DELETE",
        { Province_Id: selectedUuid },
        {},
        {},
        navigate,
        "/provinces",
        dispatch
      );
      toast.success("Province deleted successfully.");

      fetchProvinces();
    } catch (error) {
      console.error("Error deleting province:", error);
      toast.error(error?.message || "Error deleting province");
      fetchProvinces();
    }
  };

  const showDeleteModal = (province_id) => {
    setSelectedUuid(province_id);
    setModalVisible(true);
  };

  const columns = [
    {
      name: <span className="text-[0.813rem]">S.NO</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
      sortable: false,
    },
    {
      name: <span className="text-[0.813rem] ">Province Name</span>,
      selector: (row) => row.province,
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      cell: (row) => {
        if (row.status === null || row.status === undefined) {
          return <span className="text-gray-400 italic">—</span>;
        }
        const isActive = row.status === 1;
        return (
          <span
            className={`py-1 px-2 text-[0.85em] rounded ${isActive ? "bg-success text-white" : "bg-red-500 text-white"
              }`}
          >
            {isActive ? "Active" : "In-Active"}
          </span>
        );
      },
    },
    {
      name: <span className="text-[0.813rem]">Action</span>,
      cell: (row) => (

        <div className="flex gap-2">
          <div className="flex gap-2">
            {/* <Link>
              <i className="fa-solid fa-eye text-primaryDark fa-lg"></i>
            </Link> */}
            <Link to={`/provinces/edit/${row.province_id}`}>
              <i className="fa-solid fa-pen-to-square fa-lg"></i>
            </Link>
          </div>
          <button onClick={() => showDeleteModal(row.province_id)}>
           <i class="fa fa-trash fa-lg text-primaryDark" aria-hidden="true"></i>
          </button>
        </div>

      ),
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  return (

    <div>
         {loader && <Loader />}
    <div className="my-4 w-full flex flex-col justify-between space-y-3">
   
        <div className="flex  lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1  className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
               Manage Provinces
             </h1>
              <Link to="add">
               <AddButton text="Add" icon={ChevronsRightIcon} />
             </Link>
           </div>

      <Accordion>
        <AccordionSummary
          expandIcon={
            isFiltersVisible ? (
              <Minus color="#fff " size={14} />
            ) : (
              <Plus size={14} color="#fff " />
            )
          }
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{ 
            backgroundColor:"#137e81",
    color: '#fff !important', // MUI sx prop with !important
    '& .MuiAccordionSummary-content': {
      color: '#fff  !important' // Target the content specifically
    }
  }}
          onClick={toggleFilters}
        >
          Filters
        </AccordionSummary>
        <AccordionDetails>
          <div className="flex gap-5 w-full">
            <div className="w-[30%]">
              <ThinDropdownWithSearch required={false}
                label="Province"
             options={
              Array.isArray(allProvinces)
                ? allProvinces.map((province) => ({
                    value: province.province_id,
                    label: province.province,
                  }))
                : []
            }
                value={selectedProvince}
                onChange={handleProvinceChange}
                required={false}
              />
            </div>
            <div className="w-[30%]">
              <ThinDropdownWithSearch required={false}
                label="Status"
                id="status-dropdown"
                options={[
                  { value: "1", label: "Active" },
                  { value: "0", label: "In-Active" },
                ]}
                value={selectedStatus}
                onChange={handleStatusChange}
                required={false}
              />
            </div>
            <div className="w-[30%] flex items-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-gray-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <br />
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4  mb-[7rem]">
         <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px]  tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
        Province List
          </h1>
        
        </div>
        <div className="p-[1.25rem] w-full">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />

            <input
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              type="search"
             className="font-[Light] text-[#212529] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none"
            />
          </div>

          <DataTable
            columns={columns}
            persistTableHead
            data={provinces}
            customStyles={customStyles}
            highlightOnHover
          />

          <div className="flex md:flex-row flex-col justify-between items-center">
            <div className="flex gap-2 justify-between items-center my-4 text-[1rem] text-[#212529]">
           <p className="font-[Regular]">
  Page: <strong  className="font-[Inter]">{pagination.currentPage}</strong> of{" "}
  <strong className="font-[Inter]">{pagination.totalPages}</strong>
</p>
            </div>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
               shape="rounded"
 sx={{
    '& .Mui-selected': {
      backgroundColor: 'none',
      color: '#137e81',
      borderBottom: '2px solid #137e81',
      '&:hover': {
           color: '#fff',
        backgroundColor: '#137e81',
      },
    },
  }}
            />
          </div>
        </div>

      </div>

      <CustomConfirmModal
        isVisible={isModalVisible}
        onConfirm={handleDelete}
        onCancel={() => setModalVisible(false)}
        message="Are you sure you want to delete this province?"
      />
    </div>
    </div>
  );
};

export default Provinces;